// ============================================================
// ByOurConsent.com -- Register Page
// ============================================================

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const RegisterPage = () => {
    const { register, resendVerification } = useAuth();
    const navigate     = useNavigate();
    const location     = useLocation();
    const from = location.state?.from?.pathname || location.state?.from || '/welcome';
    const [form, setForm] = useState({
        first_name: '', last_name: '', email: '',
        password: '', confirm_password: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors,  setErrors]  = useState({});
    const [registered, setRegistered] = useState(false);
    const [resending,  setResending]  = useState(false);
    const [resent,     setResent]     = useState(false);

    const handleChange = (e) => {
        const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm(p => ({ ...p, [e.target.name]: val }));
        if (errors[e.target.name]) setErrors(p => ({ ...p, [e.target.name]: null }));
    };

    const validate = () => {
        const errs = {};
        if (!form.first_name.trim()) errs.first_name = 'First name required';
        if (!form.last_name.trim())  errs.last_name  = 'Last name required';
        if (!form.email)             errs.email      = 'Email required';
        if (form.password.length < 8) errs.password  = 'Password must be at least 8 characters';
        if (form.password !== form.confirm_password)
            errs.confirm_password = 'Passwords do not match';
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setLoading(true);
        setErrors({});
        try {
            await register({
                first_name:           form.first_name.trim(),
                last_name:            form.last_name.trim(),
                email:                form.email,
                password:             form.password
            });
            setRegistered(true);
        } catch (err) {
            const msg = err.message || 'Registration failed. Please try again.';
            setErrors({ general: msg });
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        try {
            await resendVerification(form.email);
            setResent(true);
        } catch {
            toast.error('Something went wrong. Please try again in a moment.');
        } finally {
            setResending(false);
        }
    };

    const field = (name, label, type = 'text', placeholder = '') => (
        <div style={{ marginBottom: '1rem' }}>
            <label style={{ display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'5px' }}>
                {label}
            </label>
            <input type={type} name={name} value={form[name]} onChange={handleChange}
                placeholder={placeholder} autoComplete={name}
                style={{ width:'100%', background:'#f5f0e8', border:`0.5px solid ${errors[name] ? '#8b1a1a' : '#c8beaa'}`, borderRadius:'3px', padding:'9px 12px', fontSize:'14px', fontFamily:'sans-serif', color:'#1e2a3a', outline:'none' }} />
            {errors[name] && (
                <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8b1a1a', marginTop:'3px' }}>{errors[name]}</div>
            )}
        </div>
    );

    return (
        <>
            <Helmet><title>Create Account — By Our Consent</title></Helmet>

            {/* Modal overlay */}
            <div style={{ position:'fixed', inset:0, background:'rgba(20,28,38,0.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem', backdropFilter:'blur(2px)' }}
                onClick={(e) => { if (e.target === e.currentTarget) navigate(-1); }}>

                <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', width:'100%', maxWidth:'460px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.4)' }}>

                    {/* Modal header */}
                    <div style={{ background:'#1e2a3a', padding:'1rem 1.5rem', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'4px 4px 0 0' }}>
                        <div>
                            <div style={{ fontSize:'10px', fontFamily:'sans-serif', letterSpacing:'0.16em', textTransform:'uppercase', color:'#8b9a7a', marginBottom:'2px' }}>Join the movement</div>
                            <div style={{ fontSize:'20px', fontWeight:600, color:'#e8dfc8', fontFamily:"'EB Garamond',Georgia,serif" }}>Create your account</div>
                            {location.state?.message && (
                                <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8b9a7a', marginTop:'8px', lineHeight:1.5 }}>
                                    {location.state.message}
                                </div>
                            )}
                        </div>
                        <button onClick={() => navigate(-1)}
                            style={{ background:'transparent', border:'none', color:'#8a9aaa', fontSize:'22px', cursor:'pointer', lineHeight:1, padding:'4px 8px' }}>
                            ×
                        </button>
                    </div>

                    {/* Form */}
                    <div style={{ padding:'1.5rem' }}>
                        {registered ? (
                            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                                <div style={{ fontSize: '40px', marginBottom: '0.75rem' }}>📬</div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond',Georgia,serif", marginBottom: '0.75rem' }}>
                                    Check your email
                                </div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                                    We sent a verification link to <strong>{form.email}</strong>. Click it to activate your account, then sign in below.
                                </div>

                                <Link to="/login" state={{ from }}
                                    style={{ display: 'block', background: '#8b1a1a', color: '#f5f0e8', padding: '12px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px', marginBottom: '10px' }}>
                                    Go to sign in →
                                </Link>

                                {resent ? (
                                    <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#3b6d11' }}>
                                        Sent again — check your inbox (and spam folder).
                                    </div>
                                ) : (
                                    <button onClick={handleResend} disabled={resending}
                                        style={{ background: 'transparent', border: 'none', color: '#6a7080', fontSize: '12px', fontFamily: 'sans-serif', textDecoration: 'underline', cursor: resending ? 'not-allowed' : 'pointer' }}>
                                        {resending ? 'Sending...' : "Didn't get it? Resend the email"}
                                    </button>
                                )}
                            </div>
                        ) : (
                        <>
                        {errors.general && (
                            <div style={{ background:'#fdf0ee', border:'0.5px solid #d4a8a0', borderRadius:'3px', padding:'10px 12px', marginBottom:'1rem', fontSize:'13px', fontFamily:'sans-serif', color:'#8b1a1a' }}>
                                {errors.general}
                            </div>
                        )}
                        <form onSubmit={handleSubmit}>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                                {field('first_name', 'First name', 'text', 'First')}
                                {field('last_name',  'Last name',  'text', 'Last')}
                            </div>
                            {field('email',            'Email address',    'email',    'your@email.com')}
                            {field('password',         'Password',         'password', '8+ characters')}
                            {field('confirm_password', 'Confirm password', 'password', 'Repeat password')}

                            <button type="submit" disabled={loading}
                                style={{ width:'100%', background: loading ? '#6a1212' : '#8b1a1a', color:'#f5f0e8', border:'none', padding:'12px', fontSize:'13px', fontFamily:'sans-serif', letterSpacing:'0.08em', textTransform:'uppercase', cursor: loading ? 'not-allowed' : 'pointer', borderRadius:'3px' }}>
                                {loading ? 'Creating account...' : 'Create account'}
                            </button>

                            <button type="button" onClick={() => navigate(-1)}
                                style={{ width:'100%', background:'transparent', color:'#6a7080', border:'0.5px solid #c8beaa', padding:'10px', fontSize:'12px', fontFamily:'sans-serif', letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', borderRadius:'3px', marginTop:'8px' }}>
                                ← Cancel
                            </button>

                            <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070', textAlign:'center', marginTop:'1rem', lineHeight:1.5 }}>
                                By creating an account you agree to our{' '}
                                <Link to="/terms" style={{ color:'#8b6a3a' }}>Terms of Service</Link>
                                {' '}and{' '}
                                <Link to="/privacy" style={{ color:'#8b6a3a' }}>Privacy Policy</Link>.
                            </div>
                        </form>
                        </>
                        )}

                        <div style={{ textAlign:'center', marginTop:'1.25rem', fontSize:'13px', fontFamily:'sans-serif', color:'#6a7080', paddingTop:'1rem', borderTop:'0.5px solid #c8beaa' }}>
                            Already have an account?{' '}
                            <Link to="/login" style={{ color:'#8b1a1a', textDecoration:'none', fontWeight:600 }}>Sign in</Link>
                        </div>
                        <div style={{ textAlign:'center', marginTop:'6px', fontSize:'11px', fontFamily:'sans-serif', color:'#9a9080' }}>
                            No party affiliation. No ads. No selling your data.
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RegisterPage;
